import { useMutation, useQueryClient } from "@tanstack/react-query";
import { appointmentKeys } from "../appointment.keys";
import { workOrderKeys } from "../../../work-order/api/work-order.keys";
import { appointmentApi } from "../service";
import { auditLogKeys } from "../../../audit-log";
import { rescheduleKeys } from "../../../reschedule/api/reschedule.keys";

/**
 * Delete an appointment, optionally the whole rest of its series.
 *
 * `forFullSeries` also removes every LATER occurrence, skipping any that is completed or already
 * started. Replaces the old series `shorten` and `cancel-remaining` hooks, which were the only
 * way to remove an appointment and could not touch a standalone one.
 *
 * Rejects with 409 (`APPOINTMENT_DELETE_FORBIDDEN`) for a completed or already-started
 * appointment — work that happened is a record, not a plan.
 */
export function useDeleteAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      forFullSeries,
    }: {
      id: number;
      forFullSeries?: boolean;
      workOrderId?: string;
    }) => {
      const { data } = await appointmentApi.remove(id, forFullSeries);
      return data;
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: rescheduleKeys.count() });
      if (vars?.workOrderId) {
        queryClient.invalidateQueries({ queryKey: auditLogKeys.byWorkOrder(vars.workOrderId) });
      }
    },
  });
}
